import { useState } from 'react';
import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { Calendar } from '@/components/ui/Calendar';
import { ThemedText, Caption } from '@/components/ui/Text';
import type { DocumentType, DocumentMetadata } from '@/services/documentVault';
import { COUNTRY_OPTIONS } from '@/data/profileOptions';
import { AutocompleteInput } from '@/components/ui/AutocompleteInput';
import { getStateSuggestions, getPlaceOfBirthSuggestions } from '@/utils/formSuggestions';

type DocumentFormFieldsProps = {
  documentType: DocumentType;
  metadata: Partial<DocumentMetadata> | null;
  onMetadataChange: (metadata: Partial<DocumentMetadata>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
};

const VISA_TYPE_OPTIONS = [
  { value: 'tourist', label: 'Tourist' },
  { value: 'business', label: 'Business' },
  { value: 'transit', label: 'Transit' },
  { value: 'student', label: 'Student' },
  { value: 'work', label: 'Work' },
  { value: 'diplomatic', label: 'Diplomatic' },
  { value: 'other', label: 'Other' },
];

const HEALTH_RECORD_TYPE_OPTIONS = [
  { value: 'vaccination', label: 'Vaccination Record' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'medical_certificate', label: 'Medical Certificate' },
  { value: 'insurance_card', label: 'Insurance Card' },
  { value: 'other', label: 'Other' },
];

const INSURANCE_TYPE_OPTIONS = [
  { value: 'travel', label: 'Travel Insurance' },
  { value: 'health', label: 'Health Insurance' },
  { value: 'life', label: 'Life Insurance' },
  { value: 'auto', label: 'Auto Insurance' },
  { value: 'other', label: 'Other' },
];

const NUMBER_OF_ENTRIES_OPTIONS = [
  { value: 'single', label: 'Single Entry' },
  { value: 'double', label: 'Double Entry' },
  { value: 'multiple', label: 'Multiple Entry' },
];

export function DocumentFormFields({
  documentType,
  metadata,
  onMetadataChange,
  errors = {},
  disabled = false,
}: DocumentFormFieldsProps) {
  const theme = useTheme();
  const [showDatePicker, setShowDatePicker] = useState<string | null>(null);

  const updateMetadata = (field: string, value: unknown) => {
    onMetadataChange({
      ...metadata,
      [field]: value,
    });
  };

  const renderDateField = (
    label: string,
    field: string,
    value: Date | null | undefined,
    required = false,
    minDate?: Date,
    maxDate?: Date
  ) => {
    const dateDisplay = value
      ? value.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : `Select ${label.toLowerCase()}`;
    
    return (
      <View>
        <ThemedText variant="bodyStrong" style={{ marginBottom: theme.spacing(0.75) }}>
          {label} {required && <ThemedText color={theme.colors.error}>*</ThemedText>}
        </ThemedText>
        <View style={{ position: 'relative' }}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`${label}${required ? ', required' : ''}, ${value ? dateDisplay : 'not set'}. Tap to change date`}
            accessibilityHint={required ? `Opens calendar picker to select ${label.toLowerCase()}. Required field.` : `Opens calendar picker to select ${label.toLowerCase()}`}
            accessibilityRequired={required}
            accessibilityState={{ disabled }}
            onPress={() => setShowDatePicker(field)}
            disabled={disabled}
            style={({ pressed }) => [
              {
                borderWidth: 1,
                borderColor: errors[field] ? theme.colors.error : theme.colors.border,
                borderRadius: theme.radii.md,
                paddingHorizontal: theme.spacing(2),
                paddingVertical: theme.spacing(1.5),
                backgroundColor: theme.colors.surface,
                opacity: (pressed || disabled) ? 0.85 : 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 44,
                paddingRight: value && !disabled ? theme.spacing(6) : theme.spacing(2),
              },
            ]}
          >
            <ThemedText
              variant="body"
              color={value ? theme.colors.text : theme.colors.textSecondary}
            >
              {dateDisplay}
            </ThemedText>
          </Pressable>
          {value && !disabled && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`Clear ${label.toLowerCase()}`}
              accessibilityHint={`Removes the selected ${label.toLowerCase()}`}
              onPress={(e) => {
                e.stopPropagation();
                updateMetadata(field, null);
              }}
              disabled={disabled}
              style={{
                position: 'absolute',
                right: theme.spacing(1.5),
                top: '50%',
                transform: [{ translateY: -9 }],
                padding: theme.spacing(0.5),
                zIndex: 1,
              }}
            >
              <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
            </Pressable>
          )}
        </View>
        {errors[field] && (
          <Caption 
            color={theme.colors.error} 
            style={{ marginTop: theme.spacing(0.5) }}
            accessibilityLiveRegion="polite"
            accessibilityRole="alert"
          >
            {errors[field]}
          </Caption>
        )}
        <Calendar
          visible={showDatePicker === field}
          onClose={() => setShowDatePicker(null)}
          onSelectDate={(date) => {
            updateMetadata(field, date);
            setShowDatePicker(null);
          }}
          selectedDate={value || undefined}
          minDate={minDate}
          maxDate={maxDate}
          title={`Select ${label}`}
        />
      </View>
    );
  };

  switch (documentType) {
    case 'passport': {
      const passportMeta = metadata as Partial<import('@/services/documentVault').PassportMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <InputField
            label="Passport Number"
            placeholder="Enter passport number"
            value={passportMeta?.number || ''}
            onChangeText={(text) => updateMetadata('number', text)}
            errorText={errors.number}
            editable={!disabled}
            autoCapitalize="characters"
          />
          <SelectField
            label="Issuing Country"
            placeholder="Select issuing country"
            value={passportMeta?.issuingCountry || ''}
            options={COUNTRY_OPTIONS}
            onValueChange={(value) => updateMetadata('issuingCountry', value)}
            errorText={errors.issuingCountry}
          />
          {renderDateField('Issue Date', 'issueDate', passportMeta?.issueDate, false)}
          {renderDateField(
            'Expiration Date',
            'expirationDate',
            passportMeta?.expirationDate,
            true,
            new Date()
          )}
          <SelectField
            label="Nationality (Optional)"
            placeholder="Select nationality"
            value={passportMeta?.nationality || ''}
            options={COUNTRY_OPTIONS}
            onValueChange={(value) => updateMetadata('nationality', value)}
            allowClear
          />
          <AutocompleteInput
            label="Place of Birth (Optional)"
            placeholder="Enter city or country"
            value={passportMeta?.placeOfBirth || ''}
            onChangeText={(text) => updateMetadata('placeOfBirth', text)}
            editable={!disabled}
            suggestions={getPlaceOfBirthSuggestions}
            minQueryLength={1}
            showSuggestionsOnFocus={true}
          />
          <InputField
            label="Full Name on Passport (Optional)"
            placeholder="Enter full name as shown on passport"
            value={passportMeta?.fullName || ''}
            onChangeText={(text) => updateMetadata('fullName', text)}
            editable={!disabled}
            autoCapitalize="words"
          />
        </View>
      );
    }

    case 'driving_license': {
      const licenseMeta = metadata as Partial<import('@/services/documentVault').DrivingLicenseMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <InputField
            label="License Number"
            placeholder="Enter license number"
            value={licenseMeta?.number || ''}
            onChangeText={(text) => updateMetadata('number', text)}
            errorText={errors.number}
            editable={!disabled}
            autoCapitalize="characters"
          />
          <AutocompleteInput
            label="Issuing State (Optional)"
            placeholder="Enter US state"
            value={licenseMeta?.issuingState || ''}
            onChangeText={(text) => updateMetadata('issuingState', text)}
            editable={!disabled}
            suggestions={getStateSuggestions}
            minQueryLength={1}
            showSuggestionsOnFocus={true}
          />
          <SelectField
            label="Issuing Country"
            placeholder="Select issuing country"
            value={licenseMeta?.issuingCountry || ''}
            options={COUNTRY_OPTIONS}
            onValueChange={(value) => updateMetadata('issuingCountry', value)}
            errorText={errors.issuingCountry}
          />
          {renderDateField('Issue Date', 'issueDate', licenseMeta?.issueDate)}
          {renderDateField(
            'Expiration Date',
            'expirationDate',
            licenseMeta?.expirationDate,
            true,
            new Date()
          )}
          <InputField
            label="License Class/Type (Optional)"
            placeholder="e.g., Class D, Commercial, etc."
            value={licenseMeta?.licenseClass || ''}
            onChangeText={(text) => updateMetadata('licenseClass', text)}
            editable={!disabled}
          />
          <InputField
            label="Address on License (Optional)"
            placeholder="Enter address as shown on license"
            value={licenseMeta?.address || ''}
            onChangeText={(text) => updateMetadata('address', text)}
            editable={!disabled}
            multiline
            numberOfLines={2}
          />
        </View>
      );
    }

    case 'visa': {
      const visaMeta = metadata as Partial<import('@/services/documentVault').VisaMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <InputField
            label="Visa Number"
            placeholder="Enter visa number"
            value={visaMeta?.number || ''}
            onChangeText={(text) => updateMetadata('number', text)}
            errorText={errors.number}
            editable={!disabled}
            autoCapitalize="characters"
          />
          <SelectField
            label="Issuing Country"
            placeholder="Select issuing country"
            value={visaMeta?.issuingCountry || ''}
            options={COUNTRY_OPTIONS}
            onValueChange={(value) => updateMetadata('issuingCountry', value)}
            errorText={errors.issuingCountry}
          />
          {renderDateField('Issue Date', 'issueDate', visaMeta?.issueDate)}
          {renderDateField(
            'Expiration Date',
            'expirationDate',
            visaMeta?.expirationDate,
            true,
            new Date()
          )}
          <SelectField
            label="Visa Type (Optional)"
            placeholder="Select visa type"
            value={visaMeta?.visaType || ''}
            options={VISA_TYPE_OPTIONS}
            onValueChange={(value) => updateMetadata('visaType', value)}
            allowClear
          />
          {renderDateField('Entry Date (Optional)', 'entryDate', visaMeta?.entryDate)}
          {renderDateField('Exit Date (Optional)', 'exitDate', visaMeta?.exitDate)}
          <SelectField
            label="Number of Entries (Optional)"
            placeholder="Select number of entries"
            value={visaMeta?.numberOfEntries || ''}
            options={NUMBER_OF_ENTRIES_OPTIONS}
            onValueChange={(value) => updateMetadata('numberOfEntries', value)}
            allowClear
          />
        </View>
      );
    }

    case 'health_record': {
      const healthMeta = metadata as Partial<import('@/services/documentVault').HealthRecordMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <SelectField
            label="Record Type"
            placeholder="Select record type"
            value={healthMeta?.recordType || ''}
            options={HEALTH_RECORD_TYPE_OPTIONS}
            onValueChange={(value) => updateMetadata('recordType', value)}
            errorText={errors.recordType}
          />
          <InputField
            label="Provider Name"
            placeholder="Enter healthcare provider name"
            value={healthMeta?.providerName || ''}
            onChangeText={(text) => updateMetadata('providerName', text)}
            errorText={errors.providerName}
            editable={!disabled}
            autoCapitalize="words"
          />
          {renderDateField('Date', 'date', healthMeta?.date, true)}
          <InputField
            label="Doctor Name (Optional)"
            placeholder="Enter doctor's name"
            value={healthMeta?.doctorName || ''}
            onChangeText={(text) => updateMetadata('doctorName', text)}
            editable={!disabled}
            autoCapitalize="words"
          />
          <InputField
            label="Medical Record Number (Optional)"
            placeholder="Enter medical record number"
            value={healthMeta?.medicalRecordNumber || ''}
            onChangeText={(text) => updateMetadata('medicalRecordNumber', text)}
            editable={!disabled}
          />
          <InputField
            label="Diagnosis (Optional)"
            placeholder="Enter diagnosis"
            value={healthMeta?.diagnosis || ''}
            onChangeText={(text) => updateMetadata('diagnosis', text)}
            editable={!disabled}
            multiline
            numberOfLines={3}
          />
          <InputField
            label="Notes (Optional)"
            placeholder="Enter additional notes"
            value={healthMeta?.notes || ''}
            onChangeText={(text) => updateMetadata('notes', text)}
            editable={!disabled}
            multiline
            numberOfLines={3}
          />
        </View>
      );
    }

    case 'insurance': {
      const insuranceMeta = metadata as Partial<import('@/services/documentVault').InsuranceMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <InputField
            label="Policy Number"
            placeholder="Enter policy number"
            value={insuranceMeta?.policyNumber || ''}
            onChangeText={(text) => updateMetadata('policyNumber', text)}
            errorText={errors.policyNumber}
            editable={!disabled}
          />
          <InputField
            label="Provider"
            placeholder="Enter insurance provider name"
            value={insuranceMeta?.provider || ''}
            onChangeText={(text) => updateMetadata('provider', text)}
            errorText={errors.provider}
            editable={!disabled}
            autoCapitalize="words"
          />
          {renderDateField(
            'Coverage Start Date',
            'coverageStartDate',
            insuranceMeta?.coverageStartDate,
            true
          )}
          {renderDateField('Coverage End Date (Optional)', 'coverageEndDate', insuranceMeta?.coverageEndDate)}
          <SelectField
            label="Policy Type (Optional)"
            placeholder="Select policy type"
            value={insuranceMeta?.policyType || ''}
            options={INSURANCE_TYPE_OPTIONS}
            onValueChange={(value) => updateMetadata('policyType', value)}
            allowClear
          />
          <InputField
            label="Coverage Details (Optional)"
            placeholder="Enter coverage details"
            value={insuranceMeta?.coverageDetails || ''}
            onChangeText={(text) => updateMetadata('coverageDetails', text)}
            editable={!disabled}
            multiline
            numberOfLines={3}
          />
          <InputField
            label="Contact Information (Optional)"
            placeholder="Enter contact information"
            value={insuranceMeta?.contactInfo || ''}
            onChangeText={(text) => updateMetadata('contactInfo', text)}
            editable={!disabled}
            multiline
            numberOfLines={2}
          />
        </View>
      );
    }

    case 'other': {
      const otherMeta = metadata as Partial<import('@/services/documentVault').OtherDocumentMetadata> | null;
      return (
        <View style={{ gap: theme.spacing(2) }}>
          <InputField
            label="Document Number"
            placeholder="Enter document number"
            value={otherMeta?.documentNumber || ''}
            onChangeText={(text) => updateMetadata('documentNumber', text)}
            errorText={errors.documentNumber}
            editable={!disabled}
          />
          <InputField
            label="Issuing Authority"
            placeholder="Enter issuing authority"
            value={otherMeta?.issuingAuthority || ''}
            onChangeText={(text) => updateMetadata('issuingAuthority', text)}
            errorText={errors.issuingAuthority}
            editable={!disabled}
            autoCapitalize="words"
          />
          {renderDateField('Issue Date (Optional)', 'issueDate', otherMeta?.issueDate)}
          {renderDateField('Expiration Date (Optional)', 'expirationDate', otherMeta?.expirationDate)}
          <InputField
            label="Notes (Optional)"
            placeholder="Enter additional notes"
            value={otherMeta?.notes || ''}
            onChangeText={(text) => updateMetadata('notes', text)}
            editable={!disabled}
            multiline
            numberOfLines={4}
          />
        </View>
      );
    }

    default:
      return null;
  }
}





